// src/components/ChangelogAdmin.js
import React, { useState, useEffect } from 'react';
import { changelogService } from '../services/changelogService';

const ChangelogAdmin = () => {
  const [changelogEntries, setChangelogEntries] = useState([]);
  const [isLoadingChangelog, setIsLoadingChangelog] = useState(true);
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Form state for new changelog entry
  const [newEntry, setNewEntry] = useState({
    version: '',
    title: '',
    description: '',
    type: 'feature',
    date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  });

  useEffect(() => {
    loadChangelogEntries();
  }, []);

  const loadChangelogEntries = async () => {
    try {
      setIsLoadingChangelog(true);
      const entries = await changelogService.getAllChangelogEntries();
      setChangelogEntries(entries);
    } catch (error) {
      console.error('Error loading changelog:', error);
    } finally {
      setIsLoadingChangelog(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEntry(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddEntry = async () => {    
    if (!newEntry.version || !newEntry.title || !newEntry.description) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setIsAddingEntry(true);
      await changelogService.addChangelogEntry(newEntry);
      
      // Reset form
      setNewEntry({
        version: '',
        title: '',
        description: '',
        type: 'feature',
        date: new Date().toISOString().split('T')[0]
      });
      
      setShowAddForm(false);
      await loadChangelogEntries(); // Refresh the list
      
      alert('Changelog entry added successfully!');
    } catch (error) {
      console.error('Error adding changelog entry:', error);
      alert('Error adding changelog entry. Please try again.');
    } finally {
      setIsAddingEntry(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this changelog entry?')) {
      return;
    }

    try {
      await changelogService.deleteChangelogEntry(entryId);
      await loadChangelogEntries(); // Refresh the list
      alert('Changelog entry deleted successfully!');
    } catch (error) {
      console.error('Error deleting changelog entry:', error);
      alert('Error deleting changelog entry. Please try again.');
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'feature': return '✨';
      case 'improvement': return '🚀';
      case 'fix': return '🐛';
      default: return '📝';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'feature': return 'text-green-600 bg-green-100';
      case 'improvement': return 'text-blue-600 bg-blue-100';
      case 'fix': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    let date;
    if (dateString && dateString.toDate) {
      date = dateString.toDate();
    } else {
      date = new Date(dateString);
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Changelog Management</h2>
              <p className="text-gray-600 mt-1">Manage application changelog entries</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                ➕ Add New Entry
              </button>
            </div>
          </div>
        </div>

        {/* Add Entry Form */}
        {showAddForm && (
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Changelog Entry</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version *
                </label>
                <input
                  type="text"
                  name="version"
                  value={newEntry.version}
                  onChange={handleInputChange}
                  placeholder="e.g., 2.1.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                <select
                  name="type"
                  value={newEntry.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="feature">✨ Feature</option>
                  <option value="improvement">🚀 Improvement</option>
                  <option value="fix">🐛 Bug Fix</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={newEntry.title}
                  onChange={handleInputChange}
                  placeholder="e.g., New Schedule View"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={newEntry.date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={newEntry.description}
                  onChange={handleInputChange}
                  placeholder="Describe the changes made in this update..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddEntry}
                  disabled={isAddingEntry}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                >
                  {isAddingEntry ? 'Adding...' : 'Add Entry'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Changelog Entries List */}
        <div className="p-6">
          {isLoadingChangelog ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : changelogEntries.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">No changelog entries found</p>
              <p className="text-gray-400 text-sm mt-2">Click "Add New Entry" to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {changelogEntries.map((entry) => (
                <div key={entry.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTypeColor(entry.type)}`}>
                          {getTypeIcon(entry.type)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900">{entry.title}</h4>
                          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            v{entry.version}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(entry.type)}`}>
                            {entry.type}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">{entry.description}</p>
                        <time className="text-sm text-gray-400">
                          {formatDate(entry.date)}
                        </time>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteEntry(entry.id)}
                      className="ml-4 text-red-600 hover:text-red-800 p-2 rounded-md hover:bg-red-50 transition-colors"
                      title="Delete entry"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogAdmin;